import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Container, Form } from "react-bootstrap";
import { TrashFill } from "react-bootstrap-icons";
import { useHistory, useLocation } from "react-router-dom";
import { HashLink as Link } from "react-router-hash-link";
import ReactSelect, { type MultiValue } from "react-select";
import {
  type DocumentSubType,
  DocumentType,
  type MiscContent,
  MiscContentType,
  PropoundingParty,
  Role,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import CustomEditor from "@/components/Editor/CustomEditor";
import LeavingGuard from "@/components/LeavingGuard";
import Loading from "@/components/Loading";
import PlaceholderTutorialLink from "@/components/PlaceholderTutorialLink";
import type { TagOption } from "@/components/Select/TagsSelect";
import SelectCreate from "@/components/Select/TagsSelectCreate";
import { useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useMiscContent from "@/hooks/useMiscContent";
import { caseTypeDisplay, getSubTypeText } from "@/lib/utils";
import styles from "./AddEditQuestion.module.scss";

/**
 * Interface for case class options used in the case type selector
 */
export interface CaseClassOption {
  label: string | null;
  value: string | null;
}

/**
 * Utility function to sort misc content by their order property
 * Handles cases where order might be undefined
 */
function sortMiscContentByOrder(a: MiscContent, b: MiscContent) {
  const aOrder = a.order;
  const bOrder = b.order;

  // Items with order come before items without order
  if (aOrder && !bOrder) {
    return -1;
  }

  if (bOrder && !aOrder) {
    return 1;
  }

  // If neither has order, maintain original order
  if (!bOrder && !aOrder) {
    return 0;
  }

  // Sort by numeric order value
  return Number(aOrder) - Number(bOrder);
}

/**
 * Main component for adding/editing discovery questions
 * Supports creating new questions, editing existing ones, and copying questions
 */
export default function AddEditQuestion() {
  const { defaultJurisdiction } = useAuth();
  // Get URL parameters for determining the type of operation
  const location = useLocation();
  const initialType = new URLSearchParams(location.search).get("type");
  const isCopying = new URLSearchParams(location.search).get("copy");

  // Custom hook for managing misc content (CRUD operations)
  const [, , , , , updateMiscContent, , createMiscContentMultiple] =
    useMiscContent();

  // Get incoming data from navigation state (for editing existing questions)
  const incoming = location.state as MiscContent[];

  // Get current user and authentication context
  const { user } = useAuth();

  // State management for tracking changes and UI state
  const [isDirty, setIsDirty] = useState<Set<number>>(new Set<number>()); // Track which questions have unsaved changes
  const [saving, setSaving] = useState(false); // Loading state during save operations
  const [questions, setQuestions] = useState<MiscContent[]>(); // Array of questions being edited
  const [availableTags, setAvailableTags] = useState<
    Map<number, string> | undefined
  >(); // Map of available tags (id -> name)
  const [addingTags, setAddingTags] = useState(new Map<number, string>()); // New tags being added
  const [alert, setAlert] = useState<string | undefined>();
  const errorRef = useRef<HTMLDivElement>(null);
  // Get user's jurisdictions and tag API
  const { jurisdictions } = useAuth();
  const tagApi = useTagApi();

  // Rich text doesn't play well with requests for now
  const useRichText = false;

  // Get the first jurisdiction (typically CA Superior Court)
  const ca_superior_court = (jurisdictions ?? [])[0];

  const history = useHistory();

  // Determine the document subtype from URL params or incoming data
  const documentSubType = initialType
    ? (Number(initialType) as DocumentSubType)
    : incoming
      ? incoming[0].documentSubType
      : undefined;

  // Extract common properties from the first question for consistency
  const caseType = questions?.[0]?.caseTypeId;
  const position = questions?.[0]?.respondentType;
  const isRepresentative = questions?.[0]?.isForRepresentative;

  // Determine if fields can be changed (only for new questions or when copying)
  const canChange = !incoming?.length || isCopying;

  /**
   * Initialize questions on component mount
   * Creates a new question if none exist, or loads existing ones
   */
  useEffect(() => {
    async function initQuestions() {
      if (!incoming?.length) {
        // Create a new question with default values
        setQuestions([
          {
            documentType: DocumentType.DiscoveryRequest,
            documentSubType: Number(initialType),
            type: MiscContentType.DiscoveryQuestion,
            order: 1,
          },
        ]);
      } else {
        if (isCopying) {
          // When copying, remove IDs and ownership to create new questions
          const copied = [...(incoming ?? [])];
          for (const x of copied) {
            x.id = undefined;
            x.ownerId = null;
            x.sharedWithFirmId = undefined;
          }
          setQuestions(copied.sort(sortMiscContentByOrder));
        } else {
          // Load existing questions for editing
          setQuestions(incoming.sort(sortMiscContentByOrder));
        }
      }
    }
    if (!questions) {
      initQuestions();
    }
  }, [initialType, questions, isCopying, incoming]);

  /**
   * Load available tags from the API on component mount
   */
  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();

      const loadedTags = await tagApi.tagFindTags({});

      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
      }
      setAvailableTags(tagsList);
    }
    loadTags();
  }, [tagApi]);

  /**
   * Save all questions to the database
   * Handles creating new tags, updating existing questions, and creating new ones
   */
  async function onSave() {
    if (disableSaveButton) {
      scrollToTop();
      if (questions?.length === 0) {
        setAlert("Please add at least one question");
        return;
      }
      setAlert("Please fill in the request and tags for each question");
      return;
    }
    setSaving(true);

    // Create any new tags that were added
    const addedTags = new Map<number, number>(); // Maps old tag index to new tag ID
    const tagsToAdd = Array.from(addingTags.entries());
    for (let i = 0; i < tagsToAdd.length; i++) {
      const t = tagsToAdd[i];
      const newTag = await tagApi.tagCreateTag({
        tagCreateRequest: { tagName: t[1] },
      });
      if (newTag) {
        addedTags.set(t[0], newTag.id);
      }
      setAvailableTags((q) => {
        const update = q ? new Map([...q]) : new Map<number, string>();
        update.set(newTag.id, newTag.name);

        return update;
      });
    }
    setAddingTags(new Map<number, string>());

    // Process each question for saving
    const toAdd: MiscContent[] = [];
    for (let i = 0; i < (questions?.length ?? 0); i++) {
      const element = questions?.[i];
      if (!element) throw new Error("Element is undefined");

      // Update tag IDs to use the newly created tag IDs
      for (const [tempId, actualId] of addedTags.entries()) {
        if (!element.tagIds) throw new Error("Tag IDs are undefined");
        const tagIndex = element.tagIds.indexOf(tempId);

        if (tagIndex !== undefined && tagIndex > -1) {
          element.tagIds[tagIndex] = actualId;
        }
      }

      // Update existing questions or mark new ones for creation
      if (
        element.id &&
        (element.isDeleted || isDirty.has(i) || isDirty.has(-1))
      ) {
        await updateMiscContent(element);
      } else {
        toAdd.push(element);
      }
    }

    // Create new questions in bulk
    if (toAdd.length) {
      await createMiscContentMultiple(toAdd);
    }

    // Reset dirty state and navigate back
    setIsDirty(new Set<number>());
    setSaving(false);
    history.goBack();
  }

  /**
   * Transform case classes into options for the case type selector
   * Creates a hierarchical structure with main classes and subtypes
   */
  const transformedOptions = useMemo(() => {
    const opts: CaseClassOption[] = [];

    opts.push({ value: null, label: "All" });
    for (const c of ca_superior_court?.caseClasses ?? []) {
      opts.push({ value: c.id, label: c.name });
      for (const s of c.subTypes ?? []) {
        opts.push({ value: s.id, label: `└${s.shortName}` });
      }
    }

    return opts;
  }, [ca_superior_court]);

  /**
   * Add a new question to the list
   * Inherits properties from the last question for consistency
   */
  function handleAddNew() {
    const lastQuestion = questions?.sort(sortMiscContentByOrder).at(-1);
    const toAdd: MiscContent = {
      type: MiscContentType.DiscoveryQuestion,
      documentType: DocumentType.DiscoveryRequest,
      documentSubType: lastQuestion?.documentSubType,
      tagIds: lastQuestion?.tagIds,
      caseTypeId: caseType,
      isForRepresentative: isRepresentative,
      respondentType: position,
      order: (lastQuestion?.order ?? 0) + 1,
      sharedWithFirmId: lastQuestion?.sharedWithFirmId,
    };
    setQuestions((q) => {
      const update = [...(q ?? [])];
      update.push(toAdd);
      return update;
    });
  }

  /**
   * Mark a question as having unsaved changes
   * @param index - Index of the question to mark as dirty
   */
  function addToIsDirty(index: number) {
    setIsDirty((d) => {
      const update = new Set<number>(d);

      update.add(index);

      return update;
    });
  }

  /**
   * Handle changes to the case type for all questions
   * @param value - New case type ID
   */
  function handleChangeType(value?: string) {
    setQuestions((qs) => {
      const update = [...(qs ?? [])];
      for (const x of update) {
        x.caseTypeId = value;
      }
      return update;
    });
    addToIsDirty(-1); // -1 indicates global changes
  }

  /**
   * Handle changes to question content
   * @param index - Index of the question being changed
   * @param value - New content value
   */
  function handleChangeContent(index: number, value: string) {
    setQuestions((qs) => {
      const update = [...(qs ?? [])];
      update[index].content = value;
      return update;
    });
    addToIsDirty(index);
  }

  /**
   * Handle changes to question tags
   * @param index - Index of the question being changed
   * @param value - New tag selection
   */
  function handleChangeTags(index: number, value: MultiValue<TagOption>) {
    let updatedQuestions: MiscContent[];
    setQuestions((qs) => {
      updatedQuestions = [...(qs ?? [])];
      updatedQuestions[index].tagIds =
        value
          ?.map((x: TagOption) => x.value)
          .filter((x: unknown): x is number => x !== null) ?? [];
      return updatedQuestions;
    });

    // Update the addingTags state to only include tags still in use
    setAddingTags((q) => {
      const update = new Map(
        [...q].filter((a) =>
          updatedQuestions.find((v) => v.tagIds?.includes(a[0])),
        ),
      );

      return update;
    });
    addToIsDirty(index);
  }

  /**
   * Handle changes to question sharing settings
   * @param index - Index of the question being changed
   * @param value - Whether to share with firm
   */
  function handleChangeShared(index: number, value: boolean) {
    setQuestions((qs) => {
      const update = [...(qs ?? [])];
      update[index].sharedWithFirmId = value ? user?.firmId : undefined;
      return update;
    });
    addToIsDirty(index);
  }

  /**
   * Remove a question from the list
   * For existing questions, mark as deleted; for new ones, remove completely
   * @param index - Index of the question to remove
   */
  function handleRemoveIem(index: number) {
    setQuestions((qs) => {
      const update = [...(qs ?? [])];
      const item = update[index];
      if (item.id) {
        // Mark existing questions as deleted (will be soft-deleted on save)
        item.isDeleted = true;
      } else {
        // Remove new questions completely
        update.splice(index, 1);
      }
      return update;
    });
  }

  /**
   * Restore a deleted question
   * @param index - Index of the question to restore
   */
  function handleRestoreItem(index: number) {
    setQuestions((qs) => {
      const update = [...(qs ?? [])];
      const item = update[index];
      item.isDeleted = false;
      return update;
    });
  }

  /**
   * Handle changes to respondent position for all questions
   * @param value - New respondent position
   */
  function handlePositionChange(value?: PropoundingParty) {
    setQuestions((qs) => {
      const update = [...(qs ?? [])];
      for (const x of update) {
        x.respondentType = value;
      }
      return update;
    });
    addToIsDirty(-1); // -1 indicates global changes
  }

  /**
   * Handle changes to representative action for all questions
   * @param value - Whether questions are for representative actions
   */
  function handleRepresentativeChange(value?: boolean) {
    setQuestions((qs) => {
      const update = [...(qs ?? [])];
      for (const x of update) {
        x.isForRepresentative = value;
      }
      return update;
    });
    addToIsDirty(-1); // -1 indicates global changes
  }

  // Redirect to library if no document subtype is specified
  if (!documentSubType) {
    history.push({
      pathname: "/library",
      search: "?type=requests",
    });
    return null;
  }

  const disableSaveButton =
    (questions?.length ?? 0) === 0 ||
    questions?.some(
      (q) => !q.content || q.content.trim() === "" || !q.tagIds?.length,
    );

  function scrollToTop() {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <>
      <LeavingGuard
        when={!!isDirty.size}
        message="You have unsaved changes, continue and lose them?"
        title="Unsaved changes"
      />
      <Container className={`page-container ${styles.libraryContainer}`}>
        <Loading isLoading={!ca_superior_court}>
          {alert && (
            <Alert ref={errorRef} variant="danger">
              {alert}
            </Alert>
          )}
          {/* Breadcrumb navigation */}
          <div className={styles.breadCrumbs}>
            <Link to={"/library"}>Library</Link>
            {" > "}
            <Link to={"/library?type=requests"}>Discovery Requests</Link>
            {" > "}
            <Link to={`/library?type=requests&document=${documentSubType}`}>
              {
                getSubTypeText(documentSubType, defaultJurisdiction?.id ?? "")
                  .plural
              }
            </Link>
            {" > "}
            {!incoming?.length ? "Create" : isCopying ? "Copy" : "Edit"}
          </div>
          {/* Page title */}
          <h2>
            {!incoming?.length ? "Add" : isCopying ? "Copy" : "Edit"}{" "}
            {
              getSubTypeText(documentSubType, defaultJurisdiction?.id ?? "")
                .plural
            }
          </h2>
          {/* Global settings section - applies to all questions */}
          <div className={styles.globalSettings}>
            {/* Case Type selector */}
            <label htmlFor="type-select" className="form-label">
              Case Type (optional)
            </label>
            <ReactSelect
              onChange={(e) => handleChangeType(e?.value ?? undefined)}
              id="type-select"
              className={styles.questionSelect}
              isDisabled={!canChange}
              options={transformedOptions}
              value={{
                value: caseType,
                label: caseTypeDisplay(caseType, ca_superior_court, true),
              }}
            />

            {/* Respondent Position selector */}
            <label htmlFor="position-select" className="form-label">
              Respondent Position
            </label>
            <Form.Group id="position-select" className={styles.formGroupCustom}>
              <Form.Check
                inline
                disabled={!canChange}
                type="radio"
                id="position-select-any"
                label="Any"
                value={undefined}
                checked={position === undefined}
                onChange={() => handlePositionChange(undefined)}
              />
              <Form.Check
                inline
                disabled={!canChange}
                type="radio"
                id="position-select-plaintiff"
                label="Plaintiff"
                value={PropoundingParty.Plaintiff}
                checked={position === PropoundingParty.Plaintiff}
                onChange={() =>
                  handlePositionChange(PropoundingParty.Plaintiff)
                }
              />
              <Form.Check
                inline
                disabled={!canChange}
                type="radio"
                id="position-select-defendant"
                label="Defendant"
                value={PropoundingParty.Defendant}
                checked={position === PropoundingParty.Defendant}
                onChange={() =>
                  handlePositionChange(PropoundingParty.Defendant)
                }
              />
            </Form.Group>

            {/* Representative Action selector */}
            <label htmlFor="representative-action" className="form-label">
              Representative Action
            </label>
            <Form.Group
              id="representative-action"
              className={styles.formGroupCustom}
            >
              <Form.Check
                inline
                disabled={!canChange}
                type="radio"
                id="representative-select-any"
                label="Any"
                value={undefined}
                checked={isRepresentative === undefined}
                onChange={() => handleRepresentativeChange(undefined)}
              />
              <Form.Check
                inline
                disabled={!canChange}
                type="radio"
                id="representative-select-ind"
                label="Individual"
                value={0}
                checked={isRepresentative === false}
                onChange={() => handleRepresentativeChange(false)}
              />
              <Form.Check
                inline
                disabled={!canChange}
                type="radio"
                id="representative-select-rep"
                label="Representative (PAGA, Class Action, etc.)"
                value={1}
                checked={isRepresentative === true}
                onChange={() => handleRepresentativeChange(true)}
              />
            </Form.Group>
          </div>
          {/* Individual questions section */}
          {questions?.sort(sortMiscContentByOrder).map((ques, index) => {
            return (
              <div className={styles.questionItem} key={`content-${index}`}>
                {!ques.isDeleted ? (
                  <>
                    {/* Delete button for each question */}
                    <TrashFill
                      onClick={() => handleRemoveIem(index)}
                      className={styles.removeItem}
                      title={!ques.id ? "Remove" : "Delete"}
                    />

                    {/* Question content editor */}
                    <Form.Group>
                      <Form.Label>Request</Form.Label>
                      <CustomEditor
                        handleChange={(val) => handleChangeContent(index, val)}
                        editorContent={ques.content ?? ""}
                        showMenu={useRichText}
                      />
                    </Form.Group>

                    {/* Tags selector for the question */}
                    <SelectCreate
                      className={styles.questionSelect}
                      id={`question-tags-${index}`}
                      label="Tags"
                      availableTags={availableTags}
                      addingTags={addingTags}
                      setAddingTags={setAddingTags}
                      selected={ques.tagIds ?? []}
                      onChange={(value) => handleChangeTags(index, value)}
                    />

                    {/* Firm sharing options - only visible to firm admins */}
                    {!!user?.role?.includes(Role.FirmAdmin) && (
                      <>
                        <span className="form-label">Add to</span>
                        <Form.Group>
                          <Form.Check
                            id="existing-share-with-firm-no"
                            inline
                            type="radio"
                            label="Personal Library"
                            onChange={(e) =>
                              handleChangeShared(index, !e.target.checked)
                            }
                            checked={!ques.sharedWithFirmId}
                          />
                          <Form.Check
                            id="existing-share-with-firm-yes"
                            type="radio"
                            inline
                            label="Firm Library"
                            onChange={(e) =>
                              handleChangeShared(index, e.target.checked)
                            }
                            checked={!!ques.sharedWithFirmId}
                          />
                        </Form.Group>
                      </>
                    )}
                  </>
                ) : (
                  /* Deleted question restoration UI */
                  <div className={styles.undoButton}>
                    Request will be deleted on Save, this cannot be undone!{" "}
                    <Button
                      variant="link"
                      onClick={() => handleRestoreItem(index)}
                    >
                      Undo
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {/* Add new question button */}
          <Button variant="outline" size="sm" onClick={handleAddNew}>
            Add Another Question
          </Button>
          {/* Help link for placeholders */}
          <PlaceholderTutorialLink />
          {/* Action buttons */}
          <div className={styles.actionButtons}>
            <Button
              variant="default"
              onClick={onSave}
              loading={saving}
              text="Saving..."
              className={disableSaveButton ? styles.disabledButton : ""}
            >
              {!isCopying ? "Save" : "Copy and Save"}
            </Button>
            <Button variant="link" onClick={() => history.goBack()}>
              Cancel
            </Button>
          </div>
        </Loading>
      </Container>
    </>
  );
}
